import type { ConfirmPopupProps } from '@neo/blocks/confirm-popup'
import { findRowFromRows } from '@neo/blocks/data-table/data-table'
import type { ContextMenuGetter, DataTableFilterField, Option } from '@neo/blocks/data-table/types'
import { Button } from '@neo/index'
import { BadgeWithTooltip } from '@neo/primitives/badge-with-tooltip'
import { Tooltip } from '@neo/primitives/tooltip'
import type { ApiResponse } from '@shared/types'
import type { ColumnDef } from '@tanstack/react-table'
import type { AxiosError } from 'axios'
import dayjs from 'dayjs'
import {
  Info,
  Layers,
  MessageCircle,
  MessageSquare,
  MoreVertical,
  PhoneIncoming,
  Send,
  Slack,
  Workflow,
} from 'lucide-react'
import type { Dispatch, SetStateAction } from 'react'
import { Link } from 'router-v6'
import { queryClient } from 'v2/configs/rq-setup'
import { FlowTriggerConfigMap } from 'v2/pages/flow/constants'
import type { RKnowledgeBase } from 'v2/pages/flow/knowledge-base/types'
import {
  agentNumbersParams,
  callEntryNamesAgent,
  isFullPage,
} from 'v2/pages/flow/tabs/agent-phone-numbers.utils'
import { AgentState } from 'v2/pages/flow/types'
import { isLegacyReadOnly } from 'v2/pages/flow/utils'
import { FlowStatusBadge } from 'v2/shared/components/flow-status-badge'
import { DeletePopupDescription } from 'v2/shared/components/misc'
import { SubaccountCell } from 'v2/shared/components/subaccount-cell'
import { useAppNavigate } from 'v2/shared/hooks/use-app-navigate'
import { checkIsDefaultFlow } from 'v2/shared/hooks/use-default-flow-check'
import { settingsService } from 'v2/shared/services/settings-service'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { isEmbedMode, toEmbedRoutePath } from 'v2/shared/utils/embed-session'
import { flowQueryKeys } from 'v2/shared/utils/query-key-management'
import { FlowTypes } from 'v2/types'
import type { RFlows, RUser } from 'v2/types/api/settings'
import { RbacModules, RbacPermissionTypes } from 'v2/types/rbac'
import { logger } from 'v2/utils/logger'
import { errorToast, successToast } from 'v2/utils/toasts'

const FullLink = ({
  to,
  children,
  type,
}: {
  to: string
  children: React.ReactNode
  type: string
}) => {
  const navigate = useAppNavigate()
  // Legacy (non-phlo) agents have no embed route, so they are not links in embed.
  if (isEmbedMode() && !type) {
    return <span className="w-full flex h-full items-center outline-0">{children}</span>
  }
  const target = toEmbedRoutePath(type ? `/agent/builder/${to}` : `/agent/legacy/${to}`)
  return (
    <Link
      tabIndex={-1}
      to={target}
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
        navigate(target, {
          state: { fromFlowsListing: true },
        })
      }}
      className="w-full flex h-full items-center outline-0"
    >
      {children}
    </Link>
  )
}

export const config = (slackTriggerEnabled: boolean, isMessagingLocked = false) => [
  {
    title: 'By Plivo',
    items: [
      {
        title: FlowTriggerConfigMap.incoming_call.title,
        description: FlowTriggerConfigMap.incoming_call.description,
        icon: <PhoneIncoming size={14} className="text-brand-text" />,
        type: FlowTypes.incoming_call,
        locked: false,
      },
      {
        title: FlowTriggerConfigMap.incoming_chat.title,
        description: FlowTriggerConfigMap.incoming_chat.description,
        icon: <MessageSquare size={14} className="text-brand-text" />,
        type: FlowTypes.incoming_chat,
        locked: false,
      },
      {
        title: FlowTriggerConfigMap.incoming_sms.title,
        description: FlowTriggerConfigMap.incoming_sms.description,
        icon: <MessageCircle size={14} className="text-brand-text" />,
        type: FlowTypes.incoming_sms,
        locked: isMessagingLocked,
      },
      {
        title: FlowTriggerConfigMap.incoming_whatsapp.title,
        description: FlowTriggerConfigMap.incoming_whatsapp.description,
        icon: <Send size={14} className="text-brand-text" />,
        type: FlowTypes.incoming_whatsapp,
        locked: isMessagingLocked,
      },
      {
        title: FlowTriggerConfigMap.api_request.title,
        description: FlowTriggerConfigMap.api_request.description,
        icon: <Workflow size={14} className="text-brand-text" />,
        type: FlowTypes.api_request,
        locked: false,
      },
    ].filter(Boolean),
  },
  {
    title: 'External Apps',
    items: [
      slackTriggerEnabled
        ? {
            title: FlowTriggerConfigMap.incoming_slack.title,
            description: FlowTriggerConfigMap.incoming_slack.description,
            icon: <Slack size={14} className="text-brand-text" />,
            type: FlowTypes.incoming_slack,
            locked: false,
          }
        : null,
    ].filter(Boolean),
  },
]

// Reverse-index the KB list by the agent it's linked to (kb.phlo_uuids) — the SAME source of truth the
// agent-builder KB tab uses. Memoized on the KB-list identity so it's built ONCE per fetch and reused
// across every flow row + re-render: O(kbs) once, then O(1) per row (a naive per-row scan would be
// O(rows × kbs)). React Query hands back a stable array ref until the list refetches (e.g. after a KB
// delete → invalidate → refetch → new ref → rebuild), so it self-refreshes without going stale.
let _kbIndexCache: {
  kbs: RKnowledgeBase[] | undefined
  byAgent: Map<string, RKnowledgeBase[]>
  reverseFailed: boolean
} | null = null
const kbLinksByAgent = (kbs?: RKnowledgeBase[]) => {
  if (_kbIndexCache && _kbIndexCache.kbs === kbs) return _kbIndexCache
  const byAgent = new Map<string, RKnowledgeBase[]>()
  // If the agent-link reverse-read failed for this list (rag phlo read errored on v2), phlo_uuids is
  // non-authoritative — callers fall back to the flow's stored ids instead.
  const reverseFailed = (kbs ?? []).some((kb) => kb.linksUnavailable)
  if (!reverseFailed) {
    for (const kb of kbs ?? []) {
      for (const agentId of kb.phlo_uuids ?? []) {
        const arr = byAgent.get(agentId)
        if (arr) arr.push(kb)
        else byAgent.set(agentId, [kb])
      }
    }
  }
  _kbIndexCache = { kbs, byAgent, reverseFailed }
  return _kbIndexCache
}

// Hidden in embed: the session writes as a machine actor, so created_by has nothing
// useful to show, and Knowledge Base is off in embed.
const EMBED_HIDDEN_COLUMNS = ['created_by', 'knowledge_base_ids']

export const columns: (
  kbs?: RKnowledgeBase[],
  subaccountNames?: Map<string, string>,
) => ColumnDef<RFlows>[] = (kbs, subaccountNames) =>
  allColumns(kbs, subaccountNames).filter(
    (col) =>
      !isEmbedMode() ||
      !EMBED_HIDDEN_COLUMNS.includes((col as { accessorKey?: string }).accessorKey ?? ''),
  )

const allColumns: (
  kbs?: RKnowledgeBase[],
  subaccountNames?: Map<string, string>,
) => ColumnDef<RFlows>[] = (kbs, subaccountNames) => [
  {
    header: 'Name',
    accessorKey: 'name',
    cell: (d) => (
      <FullLink to={d.row.original.id} type={d.row.original.phlo_type}>
        <span className="flex items-center gap-1.5 min-w-0">
          <span className="truncate" title={d.row.original.name}>
            {d.row.original.name}
          </span>
          {isLegacyReadOnly(d.row.original) && (
            <Tooltip content="This is a legacy PHLO. Duplicate it to create an editable Agent.">
              <Info className="h-4 w-4 shrink-0 text-secondary" data-testid="icon-legacy-phlo" />
            </Tooltip>
          )}
        </span>
      </FullLink>
    ),
  },
  {
    header: 'Status',
    accessorKey: 'state',
    filterFn: (row, _, value) => value.includes(row.original.state),
    cell: (d) => {
      const { state } = d.row.original
      return (
        <FullLink to={d.row.original.id} type={d.row.original.phlo_type}>
          <FlowStatusBadge state={state} />
        </FullLink>
      )
    },
  },
  {
    header: 'Trigger',
    accessorKey: 'phlo_type',
    filterFn: (row, _, value) => value.includes(row.original.phlo_type),
    cell: (d) => {
      const phloType = d.row.original.phlo_type

      if (phloType === 'omni') {
        return (
          <FullLink to={d.row.original.id} type={phloType}>
            <div className="flex items-center gap-2">
              <Layers size={14} className="text-brand-text" />
              <span>Multi-channel</span>
            </div>
          </FullLink>
        )
      }

      const triggerInfo = FlowTriggerConfigMap[phloType as FlowTypes]
      if (!triggerInfo) return null

      return (
        <FullLink to={d.row.original.id} type={phloType}>
          <div className="flex items-center gap-2">
            {triggerInfo.icon}
            <span>{triggerInfo.title}</span>
          </div>
        </FullLink>
      )
    },
  },
  // Only worth a column on accounts that actually have subaccounts — same rule the
  // Sub Account filter below uses, so the two appear and disappear together. Keyed
  // on the name map having loaded, so it settles with the subaccount fetch.
  ...(subaccountNames?.size
    ? [
        {
          header: 'Sub Account',
          accessorKey: 'sub_auth_id',
          cell: (d) => (
            <SubaccountCell subAuthId={d.row.original.sub_auth_id} names={subaccountNames} />
          ),
        } satisfies ColumnDef<RFlows>,
      ]
    : []),
  {
    header: 'Created By',
    accessorKey: 'created_by',
    filterFn: (row, _, value) => value.includes(row.original.created_by?.email),
    cell: (d) => (
      <Tooltip content={d.row.original.created_by?.email}>
        <div className="capitalize">{d.row.original.created_by?.name}</div>
      </Tooltip>
    ),
  },
  {
    header: 'Knowledge Bases',
    accessorKey: 'knowledge_base_ids',
    cell: (d) => {
      const flowId = d.row.original.id
      const { byAgent, reverseFailed } = kbLinksByAgent(kbs)
      // Count the KBs ACTUALLY linked to this agent right now (reverse-read from the live KB list, same
      // as the agent-builder KB tab) — NOT the flow's stored knowledge_base_ids, which keep listing
      // soft-deleted KBs (→ inflated counts) and can lag the real links (→ blank when the agent does
      // have KBs). Fall back to the stored ids (filtered to live KBs) only if the reverse-read failed.
      const linked = reverseFailed
        ? (kbs ?? []).filter((kb) => d.row.original.knowledge_base_ids?.includes(kb.id))
        : (byAgent.get(flowId) ?? [])
      if (!linked.length) return null
      return (
        <FullLink to={flowId} type={d.row.original.phlo_type}>
          {linked.length > 1 ? (
            <BadgeWithTooltip
              items={linked}
              selectedItemsLength={linked.length}
              moreText="knowledge base"
              findFunction={(items) => items}
              renderer={(items) =>
                items.map((item) => (
                  <div key={item.id} className="flex items-center gap-2 mb-2">
                    {item.name}
                  </div>
                ))
              }
            />
          ) : (
            <div className="flex items-center gap-2">{linked[0].name}</div>
          )}
        </FullLink>
      )
    },
  },
  {
    header: 'Updated At',
    accessorKey: 'updated_at',
    cell: (d) => {
      const updatedAt = d.row.original.updated_at
      return (
        <FullLink to={d.row.original.id} type={d.row.original.phlo_type}>
          <span className="font-jetbrains text-xs-400 tabular-nums">
            {dayjs(updatedAt * 1000).format('MMM D, YYYY h:mm A')}
          </span>
        </FullLink>
      )
    },
  },
  {
    header: '',
    accessorKey: 'actions',
    cell: () => (
      <div className="relative flex justify-end" tabIndex={-1}>
        <Button
          variant="ghost"
          tabIndex={-1}
          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 [.context-menu-open_&]:opacity-100" // Note to future dev: This is to ensure the menu is visible when the context menu is open
          onClick={(e) => {
            e.stopPropagation()
            const rect = e.currentTarget.getBoundingClientRect()
            const customEvent = new MouseEvent('contextmenu', {
              bubbles: true,
              cancelable: true,
              clientX: rect.left + 30,
              clientY: rect.bottom,
            })
            e.currentTarget.dispatchEvent(customEvent)
          }}
        >
          <MoreVertical size={20} />
          <span className="sr-only">Open menu</span>
        </Button>
      </div>
    ),
  },
]

export const getFilterFields: (
  adminUsers: RUser[],
  subAccountOptions?: Option[],
) => DataTableFilterField<RFlows>[] = (adminUsers, subAccountOptions) => [
  {
    label: 'Name',
    value: 'name',
    paramKey: 'name',
    placeholder: 'Search by name...',
  },
  {
    label: 'Status',
    value: 'state',
    paramKey: 'status',
    multiSelect: false,
    options: [
      { label: 'Active', value: AgentState.ACTIVE },
      { label: 'Paused', value: AgentState.PAUSED },
      { label: 'Draft', value: AgentState.DRAFT },
    ],
  },
  {
    label: 'Trigger',
    value: 'phlo_type',
    paramKey: 'phlo_type',
    // The trigger filter only needs each trigger's label/type; the `locked` flag is
    // irrelevant here, so pass false (the real messaging lock lives in the create-trigger view).
    // Single pass: flatMap drops the falsy entries by returning [] for them, so this
    // replaces the old flatMap -> filter -> map chain (react-doctor js-combine-iterations).
    options: config(true, false).flatMap((group) =>
      group.items.flatMap((item) => (item ? [{ label: item.title, value: item.type }] : [])),
    ),
    className: 'w-[350px]', // Custom width for trigger filter dropdown
  },
  // No user attribution in embed (column hidden, useUsersQuery off), so no filter either.
  ...(isEmbedMode()
    ? []
    : ([
        {
          label: 'Created By',
          value: 'created_by',
          paramKey: 'created_by',
          pluralLabel: 'users',
          options: adminUsers.map((user) => ({
            label: user.account_name,
            value: user.aom_uuid,
            tooltip: user.email,
          })),
        },
      ] as DataTableFilterField<RFlows>[])),
  // Subaccount filter, sent to get_all_flows as `sub_auth_id`. Options come from
  // toSubaccountFilterOptions, which leads with "Main Account" (untagged agents) —
  // only shown once the subaccount list has loaded.
  ...(subAccountOptions?.length
    ? [
        {
          label: 'Sub Account',
          value: 'sub_auth_id' as const,
          paramKey: 'sub_auth_id',
          // Multi-select: the param goes to the backend comma-separated and the ORM
          // filters with IN, so several subaccounts can be selected at once.
          multiSelect: true,
          options: subAccountOptions,
        },
      ]
    : []),
]

type HandleActionType = (
  action: string,
  setPopupState: {
    setPopupState: Dispatch<SetStateAction<ConfirmPopupProps>>
    setDuplicateFlowDialogOpen: Dispatch<SetStateAction<string>>
    setRenameFlowState: Dispatch<
      SetStateAction<{ open: boolean; flowId: string; flowName: string }>
    >
    navigate: (to: string, options?: { state?: Record<string, unknown> }) => void
  },
  flow: RFlows,
  elementRef?: React.RefObject<HTMLElement | null>,
) => void

// `flow.numbers` never shows a number attached through the v2 number route (core sends `[]` on
// every agents-list row), so embed asks core for the agent's own numbers (no list cap). Any
// error, a 404 included, counts as having numbers: a blocked delete is recoverable, a dangling
// number is not.
const embedAgentHasNumbers = async (agentId: string) => {
  try {
    const { data } = await settingsService.getAccountNumbers(agentNumbersParams(agentId))
    const rows = data ?? []
    // A full page from an older core (agent_id ignored) may hide numbers past the cap.
    return rows.some((row) => callEntryNamesAgent(row, agentId)) || isFullPage(rows)
  } catch (error) {
    logger.warn('embedAgentHasNumbers: agent number lookup failed, blocking delete', {
      error,
      flow_id: agentId,
    })
    return true
  }
}

const handleAction: HandleActionType = async (actionType, setPopupState, flow, elementRef) => {
  if (actionType === 'Open') return elementRef?.current?.click()

  if (actionType === 'Duplicate') {
    setPopupState.setDuplicateFlowDialogOpen(flow.id)
    return
  }

  if (actionType === 'Rename') {
    setPopupState.setRenameFlowState({ open: true, flowId: flow.id, flowName: flow.name })
    return
  }

  if (actionType === 'Delete') {
    // Check if flow is default flow
    const { isDefaultFlow, defaultForChannels } = await checkIsDefaultFlow(flow.id)

    if (isDefaultFlow) {
      setPopupState.setPopupState({
        open: true,
        title: 'Cannot delete default agent',
        description: (
          <div>
            This agent cannot be deleted because it is set as the default agent for the following
            channel(s):
            <ul className="mt-2 list-disc pl-5">
              {defaultForChannels.map((channel) => (
                <li key={channel}>
                  {channel.charAt(0).toUpperCase() + channel.slice(1).toLowerCase()}
                </li>
              ))}
            </ul>
            To delete this agent, first update the channel settings to use a different default
            agent.
          </div>
        ),
        okButtonProps: {
          btnTxt: 'OK',
        },
      })
      return
    }

    // Check if agent is associated with any phone numbers
    const hasNumbers =
      !!flow.numbers?.length || (isEmbedMode() && (await embedAgentHasNumbers(flow.id)))
    if (hasNumbers) {
      setPopupState.setPopupState({
        open: true,
        title: 'Cannot delete agent',
        description: (
          <div>
            This agent is associated with one or more phone numbers. Please remove the association
            before deleting this agent.
          </div>
        ),
        okButtonProps: {
          btnTxt: 'OK',
        },
      })
      return
    }

    // Existing application check
    if (flow.applications?.length) {
      setPopupState.setPopupState({
        open: true,
        title: `${actionType} Agent`,
        description: (
          <div>
            This agent is associated with one or more chat applications. Please remove the
            association in chat app settings before deleting this agent.
          </div>
        ),
        okButtonProps: {
          btnTxt: 'OK',
        },
      })
      return
    }
  }

  // Check if the agent is a default agent before pausing
  if (actionType === 'Pause') {
    const { isDefaultFlow, defaultForChannels } = await checkIsDefaultFlow(flow.id)

    if (isDefaultFlow) {
      setPopupState.setPopupState({
        open: true,
        title: 'Cannot Pause Default Agent',
        description: (
          <div>
            This agent cannot be paused because it is set as the default agent for the following
            channel(s):
            <ul className="mt-2 list-disc pl-5">
              {defaultForChannels.map((channel) => (
                <li key={channel}>
                  {channel.charAt(0).toUpperCase() + channel.slice(1).toLowerCase()}
                </li>
              ))}
            </ul>
            To pause this agent, first update the channel settings to use a different default agent.
          </div>
        ),
        okButtonProps: {
          btnTxt: 'OK',
        },
      })
      return
    }
  }

  const handleFlowAction = async (flowAction: string, flowId: string) => {
    const actionMap = {
      Delete: {
        service: () => settingsService.deleteFlow(flowId),
        updateData: (data?: RFlows[]) => data?.filter((d) => d.id !== flowId),
        success: 'The agent has been deleted successfully',
        error: 'Failed to delete the agent. Please try again.',
      },
      Pause: {
        service: () => settingsService.pauseFlow(flowId),
        updateData: (data?: RFlows[]) =>
          data?.map((d) => (d.id === flowId ? { ...d, state: AgentState.PAUSED } : d)),
        success: 'The agent has been paused successfully',
        error: 'Failed to pause the agent. Please try again.',
      },
      Publish: {
        service: () => settingsService.resumeFlow(flowId),
        updateData: (data?: RFlows[]) =>
          data?.map((d) => (d.id === flowId ? { ...d, state: AgentState.ACTIVE } : d)),
        success: 'The agent has been published successfully',
        error: 'Failed to publish the agent. Please try again.',
      },
    }

    const actionConfig = actionMap[flowAction as keyof typeof actionMap]
    if (!actionConfig) return

    try {
      await actionConfig.service()
      // Invalidate the paginated flows query to refresh the table
      queryClient.invalidateQueries({ queryKey: flowQueryKeys().flowsBase })
      successToast({
        title: 'Success',
        description: actionConfig.success,
      })
    } catch (error) {
      logger.warn('Error updating flow', { error })
      const errorText = getErrorText(error as AxiosError<ApiResponse<{ message: string }>>)
      errorToast({
        title: 'Error',
        description: errorText || actionConfig.error,
      })
    }
  }

  setPopupState.setPopupState({
    open: true,
    title: `${actionType} Agent`,
    description: <DeletePopupDescription name={flow?.name} action={actionType.toLowerCase()} />,
    okButtonProps: {
      variant: actionType === 'Delete' ? 'destructive' : undefined,
      onClick: () => handleFlowAction(actionType, flow.id),
    },
  })
}

// Get context menu for agents
export const getFlowsContextMenu: ContextMenuGetter<RFlows> = (
  flow,
  flows,
  elementRef,
  { setPopupState, setDuplicateFlowDialogOpen, setRenameFlowState, navigate },
  hasPermission,
) => {
  const [OPEN, DUPLICATE, PUBLISH, PAUSE, DELETE, COPY_AGENT_URL, RENAME] = [
    'Open',
    'Duplicate',
    'Publish',
    'Pause',
    'Delete',
    'Copy Trigger URL',
    'Rename',
  ]
  // Only migrated legacy agents (still owned by the old console) are read-only;
  // duplicates of legacy agents get the full action set.
  const isLegacy = isLegacyReadOnly(flow)
  const isDefaultAgent = !!flow?.is_default
  // Legacy agents are read-only except Duplicate; non-legacy keep main's
  // default-aware Rename + Duplicate. SER-4772 / SER-4656.
  const actions = isLegacy
    ? [OPEN, DUPLICATE]
    : [OPEN, ...(isDefaultAgent ? [] : [RENAME]), DUPLICATE]

  if (flow?.phlo_type === FlowTypes.api_request) {
    actions.push(COPY_AGENT_URL)
  }

  // Add Publish/Pause actions based on agent state (not for legacy flows)
  if (!isLegacy) {
    if (flow?.state === AgentState.DRAFT || flow?.state === AgentState.PAUSED) {
      actions.push(PUBLISH)
    } else if (flow?.state === AgentState.ACTIVE) {
      actions.push(PAUSE)
    }
  }

  if (!flow?.is_default) {
    actions.push(DELETE)
  }

  if (!hasPermission(RbacModules.aiAgent, RbacPermissionTypes.delete) && actions.includes(DELETE)) {
    actions.splice(actions.indexOf(DELETE), 1)
  }

  if (!hasPermission(RbacModules.aiAgent, RbacPermissionTypes.edit)) {
    const toRemove = [PUBLISH, PAUSE, DUPLICATE, RENAME]
    toRemove.forEach((action) => {
      const idx = actions.indexOf(action)
      if (idx !== -1) actions.splice(idx, 1)
    })
  }

  const items = [
    { type: 'label', label: 'Actions' },
    ...actions.map((action) => ({
      type: 'item',
      label: action,
      // short cut key for each action (first letter of action), 'r' for rename, and backspace key for delete action
      shortcut: (() => {
        if (action === DELETE) {
          return { key: 'backspace' }
        }
        if (action === RENAME) {
          return { key: 'r' }
        }
        return { key: action[0].toLowerCase() }
      })(),
      disabled: false,
      onlyOnHover: true,
      onSelect: () => {
        const selectedFlow = findRowFromRows(flows, {
          id: elementRef?.current?.dataset.hover || '',
        })
        if (!selectedFlow) return
        if (action === DELETE && selectedFlow.is_default) return
        if (action === PUBLISH && selectedFlow.state === AgentState.ACTIVE) return
        if (action === PAUSE && selectedFlow.state === AgentState.PAUSED) return
        if (action === COPY_AGENT_URL) {
          navigator.clipboard.writeText(selectedFlow.url)
          successToast({
            title: 'Success',
            description: 'Trigger URL copied to clipboard',
          })
          return
        }

        handleAction(
          action,
          { setPopupState, setDuplicateFlowDialogOpen, setRenameFlowState, navigate },
          selectedFlow,
          elementRef,
        )
      },
    })),
  ]

  return items
}
